import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Broadcast.css';
import { CustomSelect } from './CustomSelect.js';
import './CustomSelect.css';
import { Send, Save, Trash2, Users, Calendar, Settings, Target, Edit2, Plus, Eye } from 'lucide-react';
import { createTeamsPayload } from './teamsFormatter.js';
import Announcement1 from '../images/AskHRAdminPortal-Wide (3).png';
import { Toast, ConfirmDialog, useToast, useConfirm } from './ToastNotification.js';

const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
    ],
  },
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'color', 'background', 'link',
];

const SIMPLE_QUILL_MODULES = {
  toolbar: {
    container: [
      ['bold', 'italic'],
      [{ 'list': 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
    ],
  },
};

const SIMPLE_QUILL_FORMATS = [
  'bold', 'italic', 'list', 'bullet', 'indent', 'link'
];

const QUERY_FIELDS = [
  { value: 'fte', label: 'FTE', type: 'text' },
  { value: 'temporaryRegularFlag', label: 'Temporary/Regular Flag', type: 'text' },
  { value: 'locationName', label: 'Location Name', type: 'text' },
  { value: 'locationGroupCode', label: 'Location Group Code', type: 'text' },
  { value: 'companyCode', label: 'Company Code', type: 'text' },
  { value: 'countryName', label: 'Country Name', type: 'text' },
  { value: 'className', label: 'Class Name', type: 'text' },
  { value: 'statusName', label: 'Status Name', type: 'text' },
  { value: 'bcWc', label: 'BC/WC', type: 'text' },
  { value: 'companyName', label: 'Company Name', type: 'text' },
  { value: 'departmentName', label: 'Department Name', type: 'text' },
  { value: 'startDate', label: 'Start Date', type: 'date' },
  { value: 'level2Name', label: 'Level 2 Name', type: 'text' },
  { value: 'level3Name', label: 'Level 3 Name', type: 'text' },
  { value: 'level4Name', label: 'Level 4 Name', type: 'text' },
  { value: 'isManager', label: 'Is Manager', type: 'boolean' },
  { value: 'hasRemoteManager', label: 'Has Remote Manager', type: 'boolean' }
];

const CONDITIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'is_any_of', label: 'Is any of' },
  { value: 'is_none_of', label: 'Is none of' },
  // Add date-specific conditions
  { value: 'before', label: 'Before', dateOnly: true },
  { value: 'after', label: 'After', dateOnly: true },
  // { value: 'between', label: 'Between', dateOnly: true },
  { value: 'in_last', label: 'In last X days', dateOnly: true },
  // { value: 'in_next', label: 'In next X days', dateOnly: true },
];

const LOGICAL_OPERATORS = [
  { value: 'AND', label: 'AND' },
  { value: 'OR', label: 'OR' },
];

function compressImage(file, maxWidth = 800, maxHeight = 600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;
        if (width > height) {
          width = maxWidth;
          height = maxWidth / aspectRatio;
        } else {
          height = maxHeight;
          width = maxHeight * aspectRatio;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(blob);
      }, 'image/jpeg', quality);
    };
    img.onerror = (error) => reject(error);
  });
}

const DEFAULT_STATE = {
  text: '',
  subject: '',
  sendTo: '',
  audienceType: 'pick_audience',
  shareWith: '',
  attachments: [],
  validatedEmails: { validEmails: [], invalidEmails: [] },
  error: { text: false, message: '' },
  optionalEditors: [{ popup_title: '', popup_content: '' }, { Link_title: '', Link_content: '' }],
  savedContent: [],
  isDragOver: false,
  isValidating: false,
  isScheduled: false,
  scheduleDate: '',
  scheduleTime: '',
  queryConditions: [{ field: '', condition: '', value: '', operator: 'AND' }],
  showQueryBuilder: false,
  userCount: 0,
};

export default function Broadcast() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [attachmentKey, setAttachmentKey] = useState(Date.now());
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [employeeData, setEmployeeData] = useState([]);
  const [audienceName, setAudienceName] = useState('');
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [editingAudienceId, setEditingAudienceId] = useState(null);
  const [showManageAudiences, setShowManageAudiences] = useState(false);
  const [matchedUsers, setMatchedUsers] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [previewJson, setPreviewJson] = useState(null);
  const [isSavingAudience, setIsSavingAudience] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const { confirmDialog, showConfirm } = useConfirm();
  const [currentUserDetails, setCurrentUserDetails] = useState(null);
  const [assignedCountries, setAssignedCountries] = useState([]);
  const [isGlobalUser, setIsGlobalUser] = useState(false);
  const [liveAudienceCounts, setLiveAudienceCounts] = useState({});

  useEffect(() => {
    const tooltips = [
      { selector: ".ql-header[value='1']", title: "Heading 1" },
      { selector: ".ql-header[value='2']", title: "Heading 2" },
      { selector: ".ql-header:not([value])", title: "Normal Text" },
      { selector: ".ql-bold", title: "Bold (Ctrl + B)" },
      { selector: ".ql-italic", title: "Italic (Ctrl + I)" },
      { selector: ".ql-underline", title: "Underline (Ctrl + U)" },
      { selector: ".ql-strike", title: "Strikethrough" },
      { selector: ".ql-color", title: "Text Color" },
      { selector: ".ql-background", title: "Highlight Color" },
      { selector: ".ql-list[value='ordered']", title: "Numbered List" },
      { selector: ".ql-list[value='bullet']", title: "Bullet List" },
      { selector: ".ql-indent[value='-1']", title: "Decrease Indent" },
      { selector: ".ql-indent[value='+1']", title: "Increase Indent" },
      { selector: ".ql-blockquote", title: "Blockquote" },
      { selector: ".ql-link", title: "Insert/Edit Link" }
    ];

    tooltips.forEach(({ selector, title }) => {
      const element = document.querySelector(selector);
      if (element) element.setAttribute("title", title);
    });

    fetchEmployeeData();
    loadSavedAudiences();
    Userdetails();
    fetchAssignedCountries();
  }, []);


  const getLiveCount = useCallback((audienceId, fallback) => {
    return liveAudienceCounts[audienceId] ?? fallback;
  }, [liveAudienceCounts]);

  const fetchAssignedCountries = async () => {
    try {
      const response = await fetch('/api/user-assigned-countries', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setIsGlobalUser(data.isGlobal);
        setAssignedCountries(data.assignedCountries);
        console.log(' Assigned countries:', data);
      }
    } catch (error) {
      console.error('Error fetching assigned countries:', error);
    }
  };



  const fetchEmployeeData = async () => {
    console.log(' [FRONTEND] Fetching employee data from /api/employees');
    try {
      const response = await fetch('/api/employees', {
        credentials: 'include'
      });

      console.log(' [FRONTEND] Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(' [FRONTEND] Received employee data:', data.length, 'employees');
        const employeeArray = Array.isArray(data) ? data : [];
        setEmployeeData(employeeArray);
      } else {
        const errorText = await response.text();
        console.error('[FRONTEND] Failed to fetch employees:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('[FRONTEND] Error fetching employee data:', error);
      setEmployeeData([]);
    }
  };

  const loadSavedAudiences = async () => {
    console.log(' [FRONTEND] Fetching audiences from /api/audiences');
    try {
      const response = await fetch('/api/audiences', {
        credentials: 'include'
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[FRONTEND] Failed to fetch audiences:', errorText);
        throw new Error(`Failed to fetch audiences: ${response.status}`);
      }
  
      const audiences = await response.json();
      console.log(' [FRONTEND] Loaded audiences:', audiences.length);
  
      //Sortby name (ascending)
      const sorted = [...audiences].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
  
      setSavedAudiences(sorted);
    } catch (error) {
      console.error('[FRONTEND] Error loading audiences:', error);
      setSavedAudiences([]);
    }
  };

  const handleAudienceDropdownFocus = useCallback(async () => {
    console.log(' Refreshing audiences list...');
    await loadSavedAudiences();
  }, []);

  const handleManageAudiencesOpen = useCallback(async () => {
    console.log(' Refreshing audiences before opening manage modal...');
    await loadSavedAudiences();
    setShowManageAudiences(true);
  }, []);

  const getFieldValues = useCallback((fieldName) => {
    if (!employeeData.length) return [];

    // Code fields need EXACT match, name fields use includes()
    const codeFields = ['companyCode', 'locationCode', 'countryCode'];

    const otherConditions = state.queryConditions?.filter(
      c => c.field !== fieldName && c.condition && c.value
    ) || [];

    let filteredPool = employeeData;

    // Base restriction for non-global users
    if (!isGlobalUser && assignedCountries.length > 0) {
      const allowedLower = assignedCountries.map(c => c.toLowerCase().trim());
      filteredPool = filteredPool.filter(emp => {
        const empLower = emp.countryName?.toLowerCase().trim() || '';
        return allowedLower.some(assigned =>
          assigned === empLower ||
          assigned.includes(empLower) ||
          empLower.includes(assigned)
        );
      });
    }

    // Apply every other active condition dynamically
    otherConditions.forEach(({ field, value }) => {
      const selectedValues = Array.isArray(value) ? value : [value];
      filteredPool = filteredPool.filter(emp => {
        const empLower = emp[field]?.toString().toLowerCase().trim() || '';
        return selectedValues.some(v => {
          const vLower = v.toString().toLowerCase().trim();

          // ✅ Exact match for code fields, includes() for name fields
          if (codeFields.includes(field)) {
            return vLower === empLower;
          }

          return (
            vLower === empLower ||
            vLower.includes(empLower) ||
            empLower.includes(vLower)
          );
        });
      });
    });

    // Special case — countryName respects assignedCountries for non-global users
    if (fieldName === 'countryName' && !isGlobalUser && assignedCountries.length > 0) {
      const allowedLower = assignedCountries.map(c => c.toLowerCase().trim());
      const values = [...new Set(filteredPool.map(emp => emp[fieldName]).filter(Boolean))];
      return values.filter(empCountry => {
        const empLower = empCountry.toLowerCase().trim();
        return allowedLower.some(assigned =>
          assigned === empLower ||
          assigned.includes(empLower) ||
          empLower.includes(assigned)
        );
      }).sort();
    }

    const values = [...new Set(filteredPool.map(emp => emp[fieldName]).filter(Boolean))];
    return values.sort();

  }, [employeeData, isGlobalUser, assignedCountries, state]);

  const calculateMatchedUsers = useCallback((conditions) => {
    if (!conditions || conditions.length === 0 || !employeeData.length) {
      return [];
    }

    const validConditions = conditions.filter(c => c.field && c.condition && c.value);
    if (validConditions.length === 0) return [];

    // ⭐ Pre-filter employee pool by assigned countries (with fuzzy match)
    const employeePool = (!isGlobalUser && assignedCountries.length > 0)
      ? employeeData.filter(emp => {
        const empLower = emp.countryName?.toLowerCase().trim() || '';
        return assignedCountries.map(c => c.toLowerCase().trim()).some(assigned =>
          assigned === empLower ||
          assigned.includes(empLower) ||
          empLower.includes(assigned)
        );
      })
      : employeeData;

    // ⭐ Use employeePool instead of employeeData
    const filtered = employeePool.filter(employee => {
      let result = true;

      for (let i = 0; i < validConditions.length; i++) {
        const cond = validConditions[i];
        let fieldValue = employee[cond.field];
        let conditionResult = false;

        const fieldDef = QUERY_FIELDS.find(f => f.value === cond.field);

        if (fieldDef?.type === 'date') {
          const employeeDate = new Date(fieldValue);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          switch (cond.condition) {
            case 'equals':
              const compareDate = new Date(cond.value);
              compareDate.setHours(0, 0, 0, 0);
              conditionResult = employeeDate.getTime() === compareDate.getTime();
              break;
            case 'not_equals':
              const notEqualDate = new Date(cond.value);
              notEqualDate.setHours(0, 0, 0, 0);
              conditionResult = employeeDate.getTime() !== notEqualDate.getTime();
              break;
            case 'before':
              conditionResult = employeeDate < new Date(cond.value);
              break;
            case 'after':
              conditionResult = employeeDate > new Date(cond.value);
              break;
            case 'between':
              const [startDate, endDate] = Array.isArray(cond.value)
                ? cond.value
                : (cond.value || '').split('|');
              if (startDate && endDate) {
                const start = new Date(startDate);
                const end = new Date(endDate);
                conditionResult = employeeDate >= start && employeeDate <= end;
              }
              break;
            case 'in_last':
              const daysAgo = parseInt(cond.value);
              const pastDate = new Date(today);
              pastDate.setDate(pastDate.getDate() - daysAgo);
              conditionResult = employeeDate >= pastDate && employeeDate <= today;
              break;
            case 'in_next':
              const daysAhead = parseInt(cond.value);
              const futureDate = new Date(today);
              futureDate.setDate(futureDate.getDate() + daysAhead);
              conditionResult = employeeDate >= today && employeeDate <= futureDate;
              break;
            default:
              conditionResult = false;
          }
        }
        else if (fieldDef?.type === 'boolean') {
          fieldValue = fieldValue === true || fieldValue === 'true' || fieldValue === 'True';
          const condValue = cond.value === 'true' || cond.value === true;
          switch (cond.condition) {
            case 'equals':
              conditionResult = fieldValue === condValue;
              break;
            case 'not_equals':
              conditionResult = fieldValue !== condValue;
              break;
            default:
              conditionResult = false;
          }
        }
        else {
          switch (cond.condition) {
            case 'equals':
              conditionResult = fieldValue === cond.value;
              break;
            case 'not_equals':
              conditionResult = fieldValue !== cond.value;
              break;
            case 'contains':
              conditionResult = fieldValue && fieldValue.toString().toLowerCase().includes(cond.value.toLowerCase());
              break;
            case 'is_any_of':
              conditionResult = Array.isArray(cond.value) && cond.value.includes(fieldValue);
              break;
            case 'is_none_of':
              conditionResult = Array.isArray(cond.value) && !cond.value.includes(fieldValue);
              break;
            default:
              conditionResult = false;
          }
        }

        if (i === 0) {
          result = conditionResult;
        } else {
          const operator = validConditions[i].operator;
          if (operator === 'AND') {
            result = result && conditionResult;
          } else if (operator === 'OR') {
            result = result || conditionResult;
          }
        }
      }

      return result;
    });

    return filtered
      .map(emp => ({
        name: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown',
        email: emp.businessEmail || emp.email || '',
        countryName: emp.countryName || '',
        countryCode: emp.countryCode || '',
        employeeID: emp.employeeId || emp.employeeID || ''
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employeeData, isGlobalUser, assignedCountries]); // ⭐ Added isGlobalUser, assignedCountries to deps

  useEffect(() => {
    if (!savedAudiences.length || !employeeData.length) return;
  
    const recalculateAll = () => {
      const liveCounts = {};
  
      for (const audience of savedAudiences) {
        if (audience.conditions && audience.conditions.length > 0) {
          const matched = calculateMatchedUsers(audience.conditions);
          liveCounts[audience.id] = matched.length;
        } else {
          liveCounts[audience.id] = audience.userCount;
        }
      }
  
      // ✅ Update UI immediately — no waiting for backend
      setLiveAudienceCounts(liveCounts);
  
      // ✅ Sync to backend in background — fire and forget, never blocks UI
      savedAudiences.forEach(audience => {
        const liveCount = liveCounts[audience.id];
        if (liveCount !== undefined && liveCount !== audience.userCount) {
          fetch(`/api/audiences/${audience.id}/sync-count`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ userCount: liveCount })
          })
            .then(() => console.log(`✅ Synced "${audience.name}": ${audience.userCount} → ${liveCount}`))
            .catch(err => console.warn(`⚠️ Sync failed for "${audience.name}":`, err.message));
        }
      });
    };
  
    recalculateAll(); // ← no async/await, runs synchronously then fires bg fetches
  }, [savedAudiences, employeeData, calculateMatchedUsers]);

  useEffect(() => {
    if (state.showQueryBuilder && state.queryConditions) {
      setIsCalculating(true);
      const timeoutId = setTimeout(() => {
        const matched = calculateMatchedUsers(state.queryConditions);
        setMatchedUsers(matched);
        updateState({ userCount: matched.length });
        setIsCalculating(false);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [state.queryConditions, state.showQueryBuilder, calculateMatchedUsers]);

  const {
    text,
    subject,
    sendTo,
    audienceType,
    shareWith,
    attachments,
    error,
    optionalEditors,
    isDragOver,
    savedContent,
    isScheduled,
    scheduleDate,
    scheduleTime,
    queryConditions,
    showQueryBuilder,
    userCount,
  } = state;

  const updateState = useCallback((newValues) => {
    setState((prevState) => ({ ...prevState, ...newValues }));
  }, []);


  const Userdetails = async () => {
    try {
      // ✅ First check login type from session
      const sessionResponse = await fetch('/api/user/info', {
        credentials: 'include'
      });
  
      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json();
  
        // ✅ If password login — just use the email from session, no SSO call needed
        if (sessionData.loginType === 'password') {
          const userDetails = {
            email: sessionData.username, // this is already the email
            displayName: sessionData.username,
            country: 'N/A'
          };
          console.log('✅ Password login user details:', userDetails);
          setCurrentUserDetails(userDetails);
          return userDetails;
        }
      }
  
      // ✅ SSO login — fetch full details from Graph
      const response = await fetch('/SSO_Cred', {
        credentials: 'include'
      });
  
      if (!response.ok) throw new Error('Failed to fetch user details');
  
      const data = await response.json();
  
      if (data.data && data.data.userProfile) {
        const userDetails = {
          email: data.data.userProfile.mail || data.data.userProfile.userPrincipalName,
          displayName: data.data.userProfile.displayName,
          country: data.data.userCountry?.value || 'N/A'
        };
  
        console.log('✅ SSO user details fetched:', userDetails);
        setCurrentUserDetails(userDetails);
        return userDetails;
      }
  
      throw new Error('Invalid response format');
  
    } catch (err) {
      console.error('❌ Error fetching user details:', err);
      return null;
    }
  };

  const handleAudienceChange = useCallback((value) => {
    updateState({
      audienceType: value,
      shareWith: '',
      queryConditions: [{ field: '', condition: '', value: '', operator: 'AND' }],
      userCount: 0,
    });
    setMatchedUsers([]);
  }, [updateState]);

  const addCondition = useCallback(() => {
    const newConditions = [...queryConditions, { field: '', condition: '', value: '', operator: 'AND' }];
    updateState({ queryConditions: newConditions });
  }, [queryConditions, updateState]);

  const removeCondition = useCallback((index) => {
    if (queryConditions.length > 1) {
      const newConditions = queryConditions.filter((_, i) => i !== index);
      updateState({ queryConditions: newConditions });
    }
  }, [queryConditions, updateState]);

  const updateCondition = useCallback((index, field, value) => {
    const newConditions = [...queryConditions];
    newConditions[index] = { ...newConditions[index], [field]: value };

    if (field === 'field' || field === 'condition') {
      newConditions[index].value = '';
    }

    updateState({ queryConditions: newConditions });
  }, [queryConditions, updateState]);

  const addValueToCondition = useCallback((index, newValue) => {
    const condition = queryConditions[index];
    const isMultiSelect = condition.condition === 'is_any_of' || condition.condition === 'is_none_of';

    if (isMultiSelect) {
      const currentValues = Array.isArray(condition.value) ? condition.value :
        condition.value ? [condition.value] : [];
      if (!currentValues.includes(newValue)) {
        updateCondition(index, 'value', [...currentValues, newValue]);
      }
    } else {
      updateCondition(index, 'value', newValue);
    }
  }, [queryConditions, updateCondition]);

  const removeValueFromCondition = useCallback((index, valueToRemove) => {
    const condition = queryConditions[index];
    const currentValues = Array.isArray(condition.value) ? condition.value : [];
    updateCondition(index, 'value', currentValues.filter(v => v !== valueToRemove));
  }, [queryConditions, updateCondition]);

  const buildQueryPreview = useMemo(() => {
    if (!queryConditions || queryConditions.length === 0) return 'No conditions set';

    const validConditions = queryConditions.filter(c => c.field && c.condition && c.value && (Array.isArray(c.value) ? c.value.length > 0 : true));
    if (validConditions.length === 0) return 'No valid conditions';

    return validConditions.map((cond, idx) => {
      const fieldLabel = QUERY_FIELDS.find(f => f.value === cond.field)?.label || cond.field;
      const condLabel = CONDITIONS.find(c => c.value === cond.condition)?.label || cond.condition;
      const valueDisplay = Array.isArray(cond.value) ? cond.value.join(', ') : cond.value;
      const prefix = idx > 0 ? ` ${queryConditions[idx].operator} ` : '';
      return `${prefix}${fieldLabel} ${condLabel} "${valueDisplay}"`;
    }).join('');
  }, [queryConditions]);

  const saveAudience = useCallback(async () => {
    if (!audienceName.trim()) {
      updateState({ error: { text: true, message: 'Please enter an audience name!' } });
      return;
    }

    const validConditions = queryConditions.filter(c => c.field && c.condition && c.value);
    if (validConditions.length === 0) {
      updateState({ error: { text: true, message: 'Please add at least one valid condition!' } });
      return;
    }

    const matched = calculateMatchedUsers(queryConditions);

    const cleanedConditions = validConditions.map((cond, index) => {
      if (index === 0) {
        const { operator, ...rest } = cond;
        return rest;
      }
      return cond;
    });

    const audienceData = {
      id: editingAudienceId || null,
      name: audienceName,
      userCount: matched.length,
      conditions: cleanedConditions,
      matchedUsers: matched,
      createdAt: editingAudienceId ? savedAudiences.find(a => a.id === editingAudienceId)?.createdAt : undefined,
    };

    setIsSavingAudience(true);

    try {
      const response = await fetch('/api/audiences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(audienceData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save audience');
      }

      const result = await response.json();
      console.log(' Audience saved:', result);

      await loadSavedAudiences();

      updateState({
        showQueryBuilder: false,
        shareWith: result.id,
        error: { text: false, message: '' }
      });
      setAudienceName('');
      setEditingAudienceId(null);
      setMatchedUsers([]);

      showToast(` Audience "${audienceName}" saved successfully!`);
    } catch (error) {
      console.error('Error saving audience:', error);
      updateState({
        error: { text: true, message: `Failed to save audience: ${error.message}` }
      });
    } finally {
      setIsSavingAudience(false);
    }
  }, [audienceName, queryConditions, editingAudienceId, savedAudiences, calculateMatchedUsers, updateState]);

  const editAudience = useCallback((audience) => {
    setAudienceName(audience.name);
    updateState({
      queryConditions: audience.conditions || [{ field: '', condition: '', value: '', operator: 'AND' }],
      userCount: audience.userCount,
      showQueryBuilder: true
    });
    setEditingAudienceId(audience.id);
    setShowManageAudiences(false);
  }, [updateState]);

  const deleteAudience = useCallback(async (audienceId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this audience? This will permanently delete the entire container.');
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/audiences/${audienceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete audience');
      }

      await loadSavedAudiences();

      if (shareWith === audienceId) {
        updateState({ shareWith: '' });
      }

      showToast('Audience deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting audience:', error);
      showToast(`Failed to delete audience: ${error.message}`, 'error');
    }
  }, [shareWith, updateState, showConfirm, showToast]);

  const clearMessage = useCallback(() => {
    setState(DEFAULT_STATE);
    setAttachmentKey(Date.now());
    setShowAdvancedOptions(false);
    setPreviewJson(null);
  }, []);

  const isQuillContentEmpty = (htmlString) => {
    if (!htmlString || htmlString.trim() === '') return true;
    const strippedText = htmlString.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
    return strippedText === '';
  };

  const extractFirstLine = (htmlString) => {
    if (!htmlString) return '';
    const div = document.createElement('div');
    div.innerHTML = htmlString;
    const firstBlock = div.querySelector('p, h1, h2, h3, li');
    if (!firstBlock) return '';
    return firstBlock.textContent.trim();
  };

  const validateFields = useCallback(() => {
    const hasManualEmails = sendTo.trim().length > 0;
    const hasSavedAudience = shareWith && shareWith.trim().length > 0;

    if (!hasManualEmails && !hasSavedAudience) {
      updateState({ error: { text: true, message: 'Please either select a saved audience OR enter manual email addresses!' } });
      return false;
    }

    if (isQuillContentEmpty(text)) {
      updateState({ error: { text: true, message: 'Announcement Message is required!' } });
      return false;
    }

    const popupTitle = optionalEditors[0]?.popup_title?.trim() || '';
    const popupContent = optionalEditors[0]?.popup_content || '';

    if ((popupTitle && isQuillContentEmpty(popupContent)) || (!popupTitle && !isQuillContentEmpty(popupContent))) {
      updateState({ error: { text: true, message: 'If Additional message title or content is filled, both are required.' } });
      return false;
    }

    const linkTitle = optionalEditors[1]?.Link_title?.trim() || '';
    const linkContent = optionalEditors[1]?.Link_content?.trim() || '';

    if ((linkTitle && !linkContent) || (!linkTitle && linkContent)) {
      updateState({ error: { text: true, message: 'If URL title or content is filled, both are required.' } });
      return false;
    }

    updateState({ error: { text: false, message: '' } });
    return true;
  }, [shareWith, text, optionalEditors, sendTo, updateState]);

  const saveMessage = useCallback(async () => {
    if (!validateFields()) return;

    let userDetails = currentUserDetails;
    if (!userDetails) {
      userDetails = await Userdetails();
      // ⭐ REMOVED THE EARLY RETURN - Continue even if fetch fails
      if (!userDetails) {
        console.warn('⚠️ Could not fetch user details, using N/A values');
        // Set default values instead of returning
        userDetails = {
          email: 'N/A',
          country: 'N/A',
          displayName: 'N/A'
        };
      }
    }

    let recipientEmails = [];

    // Get emails from saved audience if selected
    if (audienceType === 'pick_audience' && shareWith) {
      const selectedAudience = savedAudiences.find(a => a.id === shareWith);
      if (selectedAudience?.matchedUsers) {
        recipientEmails = selectedAudience.matchedUsers.map(u => u.email).filter(Boolean);
      }
    }

    // Add manual emails if provided (independent of audience selection)
    if (audienceType === 'pick_audience' && sendTo.trim()) {
      const manualEmails = sendTo.split(';').map(email => email.trim()).filter(Boolean);
      recipientEmails = [...recipientEmails, ...manualEmails];
    }

    const teamsPayload = createTeamsPayload(
      subject,
      text,
      recipientEmails.join(';'),
      attachments,
      optionalEditors,
      {}
    );

    const messageData = {
      ...teamsPayload,
      EditorSubject: extractFirstLine(text),
      audienceType,
      shareWith,
      scheduledDateTime: isScheduled && scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}:00` : null,
      status: 'draft',
      savedAt: new Date().toISOString(),
      currentUser: {
        email: userDetails.email || 'N/A',
        country: userDetails.country || 'N/A',
        displayName: userDetails.displayName || 'N/A'
      },
      savedDate: new Date().toISOString(),
      savedDateFormatted: new Date().toLocaleString('en-GB', {
        timeZone: 'UTC',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }) + ' UTC',
    };

    setPreviewJson(messageData);
    updateState({
      savedContent: [messageData],
      error: { text: false, message: '' }
    });

    console.log('📋 Draft saved with Teams formatting:', messageData);
    setShowJsonPreview(true);
  }, [validateFields, subject, text, sendTo, attachments, optionalEditors, audienceType, shareWith, isScheduled, scheduleDate, scheduleTime, savedAudiences, updateState, currentUserDetails]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!validateFields()) return;

      let userDetails = currentUserDetails;
      if (!userDetails) {
        userDetails = await Userdetails();
        if (!userDetails) {
          console.warn('⚠️ Could not fetch user details, using N/A values');
          userDetails = {
            email: 'N/A',
            country: 'N/A',
            displayName: 'N/A'
          };
        }
      }

      if (isScheduled) {
        if (!scheduleDate || !scheduleTime) {
          updateState({ error: { text: true, message: 'Please select both date and time for scheduling!' } });
          return;
        }

        const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
        const now = new Date();

        if (scheduledDateTime <= now) {
          updateState({ error: { text: true, message: 'Scheduled time must be in the future!' } });
          return;
        }
      }

      let recipientEmails = [];

      if (audienceType === 'pick_audience' && shareWith) {
        const selectedAudience = savedAudiences.find(a => a.id === shareWith);
        if (selectedAudience?.matchedUsers) {
          recipientEmails = selectedAudience.matchedUsers.map(u => u.email).filter(Boolean);
        }
      }

      if (audienceType === 'pick_audience' && sendTo.trim()) {
        const manualEmails = sendTo.split(';').map(email => email.trim()).filter(Boolean);
        recipientEmails = [...recipientEmails, ...manualEmails];
      }

      const teamsPayload = createTeamsPayload(
        subject,
        text,
        recipientEmails.join(';'),
        attachments,
        optionalEditors,
        {}
      );

      const payload = {
        ...teamsPayload,
        EditorSubject: extractFirstLine(text),
        audienceType,
        scheduledDateTime: isScheduled && scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}:00` : null,
        currentUser: {
          email: userDetails.email || 'N/A',
          country: userDetails.country || 'N/A',
          displayName: userDetails.displayName || 'N/A',
        },
        sentDate: new Date().toISOString(),
        sentDateFormatted: new Date().toLocaleString('en-GB', {
          timeZone: 'UTC',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
      }) + ' UTC',

      };

      if (audienceType === 'pick_audience') {
        const selectedAudience = savedAudiences.find(a => a.id === shareWith);
        if (selectedAudience) {
          payload.audienceDetails = selectedAudience;
          payload.shareWith = shareWith;
          payload.userCount = selectedAudience.userCount;
        }
      }

      console.log('📤 Sending Teams-formatted payload:', payload);

      try {
        const response = await fetch('/api/notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send message');
        }

        const result = await response.json();
        console.log(isScheduled ? 'Message scheduled successfully:' : 'Message sent successfully:', result);

        // ✅ FIXED: Keep message content and attachments, but reset recipients
        updateState({
          error: { text: false, message: '' },
          sendTo: '',  // Clear manual email addresses
          shareWith: '',  // Clear saved audience selection
          audienceType: 'pick_audience'  // Reset audience type
        });
        showToast(`✅ Message ${isScheduled ? 'scheduled' : 'sent'} successfully!`);

      } catch (err) {
        console.error('Error sending message:', err);
        updateState({ error: { text: true, message: `Failed to send: ${err.message}` } });
      }
    },
    [validateFields, audienceType, shareWith, savedAudiences, text, subject, sendTo, attachments, optionalEditors, isScheduled, scheduleDate, scheduleTime, updateState, currentUserDetails]
  );

  const handleAttachmentChange = useCallback(async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const compressedBase64 = await compressImage(file);
        updateState({ attachments: [compressedBase64] });
        setAttachmentKey(Date.now());
      } catch (err) {
        console.error('Error compressing image:', err);
      }
    } else {
      console.warn('Only image files are allowed');
    }
  }, [updateState]);

  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    updateState({ isDragOver: false });
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const compressedBase64 = await compressImage(file);
        updateState({ attachments: [compressedBase64] });
        setAttachmentKey(Date.now());
      } catch (err) {
        console.error('Error converting file to base64:', err);
      }
    } else {
      console.warn('Only image files are allowed');
    }
  }, [updateState]);

  const handleDeleteAttachment = useCallback(() => {
    updateState({ attachments: [] });
    setAttachmentKey(Date.now());
  }, [updateState]);

  const handleAdditionalEditorChange = useCallback((index, field, value) => {
    setState((prevState) => {
      const newEditors = [...prevState.optionalEditors];
      newEditors[index] = { ...newEditors[index], [field]: value };
      return { ...prevState, optionalEditors: newEditors };
    });
  }, []);

  const handleQuillChange = useCallback((content) => {
    updateState({ text: content });
  }, [updateState]);

  return (
    <div className="wrapper">
      {/* <div className="header-wrapper">
        <div className="header">
          <img
            src={Announcement1}
            alt="Announcement Broadcast"
            className="header-logo"
          />
        </div>
      </div> */}
      <div className="header-wrapper">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px 0 24px 0', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'relative', zIndex: 10 }}>

          {/* <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '99px', padding: '4px 12px', fontSize: '11px', fontWeight: 700, color: '#60a5fa', letterSpacing: '0.08em', textTransform: 'uppercase', width: 'fit-content' }}>
      <span style={{ width: '7px', height: '7px', background: '#3b82f6', borderRadius: '50%', display: 'inline-block' }} />
      Live
    </div> */}

          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', margin: 0, letterSpacing: '-0.5px', lineHeight: 1.2, position: 'relative', zIndex: 10 }}>
            Broadcast Message
          </h1>

          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, position: 'relative', zIndex: 10 }}>
            Compose and send notifications to your target audience
          </p>

        </div>
      </div>

      <div className="container">
        <form className="form" onSubmit={handleSubmit}>
          <div className="main-layout">
            <div className="left-column">
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Target Audience <span style={{ color: '#dc2626' }}>*</span></h2>
                </div>

                <div className="input-group">
                  <label className="label">Manual Email Addresses</label>
                  <input
                    type="text"
                    value={sendTo}
                    onChange={(e) => updateState({ sendTo: e.target.value })}
                    placeholder='📧 Enter emails separated by semicolons (;)'
                    className="input"
                  />
                  <p className="input-hint" style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    You can send to manual emails only, or combine with a saved audience below
                  </p>
                </div>

                <div className="audience-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setAudienceName('');
                      setEditingAudienceId(null);
                      updateState({
                        showQueryBuilder: true,
                        queryConditions: [{ field: '', condition: '', value: '', operator: 'AND' }]
                      });
                    }}
                    className="btn-primary"
                  >
                    <Plus size={16} />
                    Create New Audience
                  </button>
                  {savedAudiences.length > 0 && (
                    <button
                      type="button"
                      onClick={handleManageAudiencesOpen}
                      className="btn-secondary"
                    >
                      <Settings size={16} />
                      Manage Audiences
                    </button>
                  )}
                </div>

                <div className="input-group">
                  <label className="label">Select Saved Audience</label>
                  <CustomSelect
                   className="csel-light"
                   lightPanel={true}
                    value={shareWith}
                    onChange={(val) => {
                      updateState({ shareWith: val });
                      handleAudienceDropdownFocus();
                    }}
                    options={savedAudiences.map(audience => ({
                      value: audience.id,
                      label: `👥 ${audience.name} (${getLiveCount(audience.id, audience.userCount)} users)`
                    }))}
                    placeholder="Choose an audience..."
                    searchable={true}
                  />
                </div>

                {shareWith && savedAudiences.find(a => a.id === shareWith) && (
                  <div className="audience-preview">
                    <div className="audience-preview-header">
                      <Users size={16} />
                      <span>Selected Audience Preview</span>
                    </div>
                    <div className="audience-stats">
                    <span className="audience-stat">
  {getLiveCount(shareWith, savedAudiences.find(a => a.id === shareWith)?.userCount)} recipients
                    </span>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          const audience = savedAudiences.find(a => a.id === shareWith);
                          if (audience && audience.id) {
                            try {
                              const response = await fetch(`/api/audiences/${audience.id}`, {
                                credentials: 'include'
                              });

                              if (response.ok) {
                                const fullAudience = await response.json();
                                setMatchedUsers(fullAudience.matchedUsers || []);
                                setShowPreviewModal(true);
                              } else {
                                showToast(`Failed to load audience details`);
                              }
                            } catch (error) {
                              showToast(`Error loading audience: ${error.message}`);
                            }
                          }
                        }}
                        className="btn-link"
                        style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                      >
                        <Eye size={14} />
                        View Details
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Announcement Message <span style={{ color: '#dc2626' }}>*</span></h2>
                </div>

                <div
                  className="quill-container"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    pointerEvents: 'auto'
                  }}
                >
                  <style>
                    {`
    .quill-container .ql-toolbar {
      background: linear-gradient(135deg, #fafbfc 0%, #f3f4f6 100%);
      border: none;
      border-bottom: 2px solid rgba(99, 102, 241, 0.1);
      padding: 12px;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }
    
    .quill-container .ql-toolbar button,
    .quill-container .ql-toolbar .ql-picker {
      pointer-events: auto !important;
      cursor: pointer !important;
      z-index: 100000 !important;
      position: relative !important;
    }
    
    .quill-container .ql-toolbar button:hover {
      background-color: #e5e7eb !important;
    }
    
    .quill-container .ql-toolbar button svg,
    .quill-container .ql-toolbar .ql-picker svg {
      pointer-events: none !important;
    }
    
    .quill-container .ql-editor {
      min-height: 110px !important;
      padding: 16px;
      font-size: 14px !important;
      line-height: 1.42 !important;
      color: #484644 !important;
      overflow-y: auto;
      word-wrap: break-word;
      overflow-wrap: break-word;
      word-break: break-word;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .quill-container .ql-editor p {
      margin: 0 !important;  /* ⭐ Remove the 12px bottom margin */
      line-height: 1.42 !important;
    }
        .quill {
      background: white;
      border-radius: 12px;
    }
    
    .quill-container .ql-editor p:last-child {
      margin-bottom: 0 !important;
    }
    
    /* SYNC FIX: Lists */
    .quill-container .ql-editor ul,
    .quill-container .ql-editor ol {
      margin: 8px 0 8px 0 !important;
      padding: 0 0 0 1.5em !important;
      list-style-position: outside !important;
    }
    
    .quill-container .ql-editor li {
      margin: 0 !important;
      padding: 0 0 0 0.5em !important;
      line-height: 1.42 !important;
    }
    
    .quill-container .ql-editor li > ul,
    .quill-container .ql-editor li > ol {
      margin: 4px 0 !important;
      padding-left: 1.5em !important;
    }
    
    /* SYNC FIX: Remove extra paragraph wrapping in lists */
    .quill-container .ql-editor li > p {
      margin: 0 !important;
      display: inline !important;
    }
    
    .quill-container .ql-editor li > p + p {
      display: block !important;
      margin-top: 8px !important;
    }
    
    /* SYNC FIX: Text formatting */
    .quill-container .ql-editor strong {
      font-weight: 600 !important;
    }
    
    .quill-container .ql-editor em {
      font-style: italic !important;
    }
    
    .quill-container .ql-editor u {
      text-decoration: underline !important;
    }
    
    .quill-container .ql-editor s {
      text-decoration: line-through !important;
    }
    
    /* SYNC FIX: Headings */
    .quill-container .ql-editor h1 {
      font-size: 2em !important;
      font-weight: 600 !important;
      margin: 0 0 12px 0 !important;
      line-height: 1.3 !important;
    }
    
    .quill-container .ql-editor h2 {
      font-size: 1.5em !important;
      font-weight: 600 !important;
      margin: 0 0 10px 0 !important;
      line-height: 1.3 !important;
    }
    
    /* SYNC FIX: Blockquotes */
    .quill-container .ql-editor blockquote {
      border-left: 4px solid #ccc !important;
      margin: 8px 0 !important;
      padding-left: 16px !important;
      color: #666 !important;
    }
    
    /* SYNC FIX: Links */
    .quill-container .ql-editor a {
      color: #6264a7 !important;
      text-decoration: none !important;
    }
    
    .quill-container .ql-editor a:hover {
      text-decoration: underline !important;
    }
    
    /* SYNC FIX: Indent classes */
    .quill-container .ql-editor .ql-indent-1,
    .quill-container .ql-editor li.ql-indent-1 {
      padding-left: 3em !important;
    }
    
    .quill-container .ql-editor .ql-indent-2,
    .quill-container .ql-editor li.ql-indent-2 {
      padding-left: 6em !important;
    }
    
    .quill-container .ql-editor .ql-indent-3,
    .quill-container .ql-editor li.ql-indent-3 {
      padding-left: 9em !important;
    }
  `}
                  </style>

                  <ReactQuill
                    value={text}
                    onChange={handleQuillChange}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Compose your message..."
                    style={{
                      pointerEvents: 'auto'
                    }}
                  />
                </div>
              </div>

              {/* //Advanced Options
                <div className="card">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                    className="advanced-toggle"
                  >
                    <Settings size={18} />
                    <span>Advanced Options</span>
                    <span className="chevron">{showAdvancedOptions ? '▼' : '▶'}</span>
                  </button>

                  {showAdvancedOptions && (
                    <div className="advanced-content">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={isScheduled}
                          onChange={(e) => updateState({ isScheduled: e.target.checked })}
                          className="checkbox"
                        />
                        <Calendar size={16} />
                        <span>Schedule for later</span>
                      </label>

                      {isScheduled && (
                        <div className="schedule-inputs">
                          <div className="input-group">
                            <label className="label">Date</label>
                            <input
                              type="date"
                              value={scheduleDate}
                              onChange={(e) => updateState({ scheduleDate: e.target.value })}
                              min={new Date().toISOString().split('T')[0]}
                              className="input"
                            />
                          </div>
                          <div className="input-group">
                            <label className="label">Time</label>
                            <input
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => updateState({ scheduleTime: e.target.value })}
                              className="input"
                            />
                          </div>
                          {scheduleDate && scheduleTime && (
                            <div className="schedule-preview">
                              ⏰ Scheduled: <strong>{new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString()}</strong>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div> */}
            </div>

            <div className="right-column">
              {/* ATTACHMENTS CARD */}
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Image</h2>
                </div>
                <div
                  className={`dropzone ${isDragOver ? 'dropzone-active' : ''}`}
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); updateState({ isDragOver: true }); }}
                  onDragLeave={() => updateState({ isDragOver: false })}
                >
                  {attachments.length === 0 ? (
                    <div className="dropzone-content">
                      <div className="upload-icon">📎</div>
                      <h3 className="dropzone-title">Drop files here</h3>
                      <input
                        type="file"
                        id="attachments"
                        key={attachmentKey}
                        onChange={handleAttachmentChange}
                        className="file-input"
                        accept="image/*"
                      />
                      <label htmlFor="attachments" className="upload-btn">
                        Click to browse
                      </label>
                    </div>
                  ) : (
                    <div className="attachment-preview">
                      <img
                        src={attachments[0]}
                        alt="attachment"
                        className="attachment-image"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteAttachment}
                        className="delete-btn"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ACTION BUTTONS CARD */}
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Actions</h2>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <button type="button" onClick={saveMessage} className="btn-save" style={{ width: '100%', justifyContent: 'center' }}>
                    <Save size={18} />
                    Save Draft
                  </button>
                  <button type="submit" className="btn-publish" style={{ width: '100%', justifyContent: 'center' }}>
                    <Send size={18} />
                    {isScheduled ? 'Schedule & Send' : 'Publish & Send'}
                  </button>
                  <button type="button" onClick={clearMessage} className="btn-clear" style={{ width: '100%', justifyContent: 'center' }}>
                    <Trash2 size={18} />
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 📱 LIVE PREVIEW CARD - MOVED BELOW MAIN LAYOUT */}
          <div className="card" style={{ marginTop: '2rem' }}>
            <div className="card-header">
              {/* <Eye size={20} className="card-icon" /> */}
              <h2 className="card-title-preview">Message Preview</h2>
            </div>

            <div className="preview-container">
              {!subject && isQuillContentEmpty(text) && attachments.length === 0 ? (
                <div className="preview-empty">
                  <Eye size={40} style={{ opacity: 0.3, marginBottom: '10px' }} />
                  <p>Start typing to see preview...</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>

                  {/* CARD 1 - Image FIXED 400px width, fixed height */}
                  {attachments.length > 0 && (
                   // AFTER - shows full image as-is
                  <div style={{
                    width: '400px',
                    borderRadius: '4px',
                    border: '1px solid #e1dfdd',
                    overflow: 'hidden',
                    boxShadow: '0 1.6px 3.6px rgba(0,0,0,0.13)',
                    flexShrink: 0
                  }}>
                    <img
                      src={attachments[0]}
                      alt="attachment"
                      style={{
                        width: '100%',
                        height: 'auto',   // ✅ lets image keep its natural proportions
                        display: 'block'
                      }}
                    />
                  </div>
                  )}

                  {/* CARD 2 - Text FULL WIDTH, grows with content */}
                  {(!isQuillContentEmpty(text) || subject) && (
                    <div style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '4px',
                      border: '1px solid #e1dfdd',
                      background: 'white',
                      boxShadow: '0 1.6px 3.6px rgba(0,0,0,0.13)',
                      fontFamily: '"Segoe UI", -apple-system, sans-serif'
                    }}>
                      {subject && (
                        <div className="teams-card-title">{subject}</div>
                      )}
                      {!isQuillContentEmpty(text) && (
                        <div
                          className="teams-card-body"
                          dangerouslySetInnerHTML={{ __html: text }}
                        />
                      )}
                      {((optionalEditors[0]?.popup_title && !isQuillContentEmpty(optionalEditors[0]?.popup_content)) ||
                        (optionalEditors[1]?.Link_title && optionalEditors[1]?.Link_content)) && (
                          <div className="teams-card-actions">
                            {optionalEditors[0]?.popup_title && !isQuillContentEmpty(optionalEditors[0]?.popup_content) && (
                              <button type="button" className="teams-action-button">
                                {optionalEditors[0].popup_title}
                              </button>
                            )}
                            {optionalEditors[1]?.Link_title && optionalEditors[1]?.Link_content && (
                              <button type="button" className="teams-action-button">
                                {optionalEditors[1].Link_title}
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>

          {error.text && (
            <div className="error-box" style={{ marginTop: '1.5rem' }}>
              <span className="error-icon">⚠️</span>
              <span>{error.message}</span>
            </div>
          )}

          {savedContent.length > 0 && (
            <div className="success-box" style={{ marginTop: '1.5rem' }}>
              <span className="success-icon"></span>
              <strong>Message Saved Successfully!</strong>
              <button
                type="button"
                onClick={() => setShowJsonPreview(true)}
                className="btn-link"
                style={{ marginLeft: '10px' }}
              >
                <Eye size={14} />
                View JSON Payload
              </button>
            </div>
          )}
        </form>
      </div>

      {showJsonPreview && previewJson && (
        <div className="modal-overlay" onClick={() => setShowJsonPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2 className="modal-title">📋 Teams Payload Preview</h2>
              <button
                type="button"
                onClick={() => setShowJsonPreview(false)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div style={{
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: '20px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '13px',
                maxHeight: '500px',
                overflow: 'auto'
              }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(previewJson, null, 2)}
                </pre>
              </div>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(previewJson, null, 2));
                    showToast(' JSON copied to clipboard!');
                  }}
                  className="btn-primary"
                >
                  📋 Copy to Clipboard
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(previewJson, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `teams-payload-${new Date().toISOString()}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="btn-secondary"
                >
                  💾 Download JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQueryBuilder && (
        <div className="modal-overlay" onClick={() => {
          updateState({ showQueryBuilder: false });
          setAudienceName('');
          setEditingAudienceId(null);
          setMatchedUsers([]);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingAudienceId ? '✏️ Edit Audience' : '🎯 Create New Audience'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  updateState({ showQueryBuilder: false });
                  setAudienceName('');
                  setEditingAudienceId(null);
                  setMatchedUsers([]);
                }}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="input-group">
                <label className="label">Audience Name *</label>
                <input
                  type="text"
                  value={audienceName}
                  onChange={(e) => setAudienceName(e.target.value)}
                  placeholder="e.g., Finland Managers, US Active Employees"
                  className="input"
                />
              </div>

              <div className="query-header">
                <h3 className="query-title">Build Query Conditions</h3>
                <p className="query-subtitle">Define rules to target specific users</p>
              </div>



              <div className="conditions-container">
                {/* ⭐ ADD THIS LABELS ROW - ONLY SHOW ONCE AT THE TOP */}
                {queryConditions.length > 0 && (
                  <div className="condition-labels">
                    <span className="small-label">FIELD</span>
                    <span className="small-label">CONDITION</span>
                    <span className="small-label">VALUE</span>
                    <span></span> {/* Empty space for delete button column */}
                  </div>
                )}

                {queryConditions.map((condition, index) => (
                  <div key={index} className="condition-wrapper">
                    {index > 0 && (
                      <div className="operator-row">
                        <CustomSelect
                         className="csel-light"
                         lightPanel={true}
                          value={condition.operator}
                          onChange={(val) => updateCondition(index, 'operator', val)}
                          options={LOGICAL_OPERATORS.map(op => ({ value: op.value, label: op.label }))}
                          placeholder="AND"
                        />
                      </div>
                    )}

                    <div className="condition-row">
                      {/* ⭐ REMOVE THE LABELS FROM HERE - No <label> tags inside condition-field */}
                      <div className="condition-field">
                        {/* ❌ DELETE THIS LINE: <label className="small-label">Field</label> */}
                        <CustomSelect
                         className="csel-light"
                         lightPanel={true}
                          value={condition.field}
                          onChange={(val) => updateCondition(index, 'field', val)}
                          options={QUERY_FIELDS.map(f => ({ value: f.value, label: f.label }))}
                          placeholder="Select..."
                        />
                      </div>

                      <div className="condition-field">
                        {/* ❌ DELETE THIS LINE: <label className="small-label">Condition</label> */}
                        <CustomSelect
                         className="csel-light"
                         lightPanel={true}
                          value={condition.condition}
                          onChange={(val) => updateCondition(index, 'condition', val)}
                          options={CONDITIONS.filter(cond => {
                            const fieldDef = QUERY_FIELDS.find(f => f.value === condition.field);
                            if (fieldDef?.type === 'date') return cond.value === 'equals' || cond.value === 'not_equals' || cond.dateOnly === true;
                            if (fieldDef?.type === 'boolean') return cond.value === 'equals' || cond.value === 'not_equals';
                            return !cond.dateOnly;
                          }).map(c => ({ value: c.value, label: c.label }))}
                          placeholder="Select..."
                        />
                      </div>

                      <div className="condition-field">
                        {/* ❌ DELETE THIS LINE: <label className="small-label">Value</label> */}
                        {condition.field && condition.condition ? (
                          (() => {
                            const fieldDef = QUERY_FIELDS.find(f => f.value === condition.field);

                            if (fieldDef?.type === 'boolean') {
                              return (
                                <CustomSelect
                                 className="csel-light"
                                 lightPanel={true}
                                  value={condition.value}
                                  onChange={(val) => updateCondition(index, 'value', val)}
                                  options={[
                                    { value: 'true', label: 'True' },
                                    { value: 'false', label: 'False' }
                                  ]}
                                  placeholder="Select..."
                                />
                              );
                            }

                            if (fieldDef?.type === 'date') {
                              if (condition.condition === 'between') {
                                const [startDate = '', endDate = ''] = Array.isArray(condition.value)
                                  ? condition.value
                                  : (condition.value || '').split('|');

                                return (
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                      type="date"
                                      value={startDate}
                                      onChange={(e) => {
                                        const newValue = [e.target.value, endDate];
                                        updateCondition(index, 'value', newValue);
                                      }}
                                      className="input"
                                      placeholder="Start date"
                                    />
                                    <span>to</span>
                                    <input
                                      type="date"
                                      value={endDate}
                                      onChange={(e) => {
                                        const newValue = [startDate, e.target.value];
                                        updateCondition(index, 'value', newValue);
                                      }}
                                      className="input"
                                      placeholder="End date"
                                    />
                                  </div>
                                );
                              }

                              if (condition.condition === 'in_last' || condition.condition === 'in_next') {
                                return (
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                      type="number"
                                      min="1"
                                      value={condition.value || ''}
                                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                      className="input"
                                      placeholder="Number of days"
                                      style={{ maxWidth: '120px' }}
                                    />
                                    <span>days</span>
                                  </div>
                                );
                              }

                              return (
                                <input
                                  type="date"
                                  value={condition.value}
                                  onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                  className="input"
                                />
                              );
                            }

                            if (condition.condition === 'is_any_of' || condition.condition === 'is_none_of') {
                              return (
                                <div>
                                  <CustomSelect
                                   className="csel-light"
                                   lightPanel={true}
                                    value=""
                                    onChange={(val) => { if (val) addValueToCondition(index, val); }}
                                    options={getFieldValues(condition.field)
                                      .filter(val => !Array.isArray(condition.value) || !condition.value.includes(val))
                                      .map(v => ({ value: v, label: v }))}
                                    placeholder="Add value..."
                                    searchable={true}
                                  />
                                  {Array.isArray(condition.value) && condition.value.length > 0 && (
                                    <div className="chip-container">
                                      {condition.value.map(val => (
                                        <span key={val} className="chip">
                                          {val}
                                          <button
                                            type="button"
                                            onClick={() => removeValueFromCondition(index, val)}
                                            className="chip-remove"
                                          >
                                            ✕
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            if (condition.condition === 'contains') {
                              return (
                                <input
                                  type="text"
                                  value={condition.value}
                                  onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                  placeholder="Enter text to search..."
                                  className="input"
                                />
                              );
                            }

                            return (
                              <CustomSelect
                               className="csel-light"
                               lightPanel={true}
                                value={condition.value}
                                onChange={(val) => updateCondition(index, 'value', val)}
                                options={getFieldValues(condition.field).map(v => ({ value: v, label: v }))}
                                placeholder="Select..."
                                searchable={true}
                              />
                            );
                          })()
                        ) : (
                          <input
                            type="text"
                            disabled
                            placeholder="Select field first"
                            className="input disabled-input"
                          />
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeCondition(index)}
                        disabled={queryConditions.length === 1}
                        className="btn-icon-danger"
                        title="Remove condition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addCondition}
                className="btn-add-condition"
              >
                <Plus size={16} />
                Add Condition
              </button>

              {queryConditions.some(c => c.field && c.condition && c.value) && (
                <>
                  <div className="query-preview-box">
                    <strong className="query-preview-label">Query Preview:</strong>
                    <code className="query-preview-code">{buildQueryPreview}</code>
                  </div>

                  <div className="user-count-box">
                    {isCalculating ? (
                      <span className="calculating">⏳ Calculating...</span>
                    ) : (
                      <>
                        <Users size={20} />
                        <span className="user-count-text">
                          <strong>{userCount}</strong> {userCount === 1 ? 'user' : 'users'} match this query
                        </span>
                        {matchedUsers.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowPreviewModal(true)}
                            className="btn-link"
                          >
                            <Eye size={14} />
                            Preview Users
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
              {error.text && (
                <div className="error-box" style={{ marginBottom: '20px' }}>
                  <span className="error-icon">⚠️</span>
                  <span>{error.message}</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={saveAudience}
                className="btn-save-audience"
                disabled={isCalculating || isSavingAudience}
              >
                <Save size={18} />
                {isSavingAudience ? 'Saving...' : (editingAudienceId ? 'Update Audience' : 'Save Audience')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManageAudiences && (
        <div className="modal-overlay" onClick={() => setShowManageAudiences(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">⚙️ Manage Saved Audiences</h2>
              <button
                type="button"
                onClick={() => setShowManageAudiences(false)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {savedAudiences.length === 0 ? (
                <p className="empty-message">No saved audiences yet.</p>
              ) : (
                <div className="audience-list">
                  {savedAudiences.map(audience => (
                    <div key={audience.id} className="audience-card">
                      <div className="audience-card-header">
                        <h3 className="audience-name">{audience.name}</h3>
                        <div className="audience-card-actions">
                          <button
                            type="button"
                            onClick={() => editAudience(audience)}
                            className="btn-icon"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteAudience(audience.id)}
                            className="btn-icon-danger"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="audience-card-body">
                        <div className="audience-meta">
                        <span className="audience-meta-item">
                        <Users size={14} />
                        {getLiveCount(audience.id, audience.userCount)} users
                      </span>
                          <span className="audience-meta-item">
                            <Calendar size={14} />
                            {new Date(audience.createdAt).toLocaleDateString('en-GB', {
                            timeZone: 'UTC',
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                        })}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/audiences/${audience.id}`, {
                                credentials: 'include'
                              });
                              if (response.ok) {
                                const fullAudience = await response.json();
                                setMatchedUsers(fullAudience.matchedUsers || []);
                                setShowPreviewModal(true);
                              }
                            } catch (error) {
                              console.error('Error fetching audience details:', error);
                            }
                          }}
                          className="btn-link"
                        >
                          <Eye size={14} />
                          View Recipients
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">👥 Matched Users ({matchedUsers.length})</h2>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="modal-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {matchedUsers.length === 0 ? (
                <p className="empty-message">No users match this query.</p>
              ) : (
                <div className="user-list">
                  {matchedUsers.map((user, index) => (
                    <div key={index} className="user-item">
                      <div className="user-avatar">
                        {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div className="user-info">
                        <div className="user-name">{user.name || 'Unknown User'}</div>
                        <div className="user-email">{user.email || 'No email available'}</div>
                        {user.countryCode && (
                          <div className="user-meta">Country: {user.countryCode}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ALL MODALS REMAIN THE SAME - Omitted for brevity but included in full file */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
}
